import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
const API_URL = process.env.REACT_APP_API_URL;
const DeveloperChargesAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState({
    receipt_no: "",
    receipt_date: new Date().toISOString().split("T")[0],
    challan_no: "",
    account_no: "",
    developer_name: "",
    amount: "",
    amount_words: "",
    fee_description: "",
    proposal_no: "",
    scheme_details_extra: "", // Kept (Scheme Details)
    payment_mode: "DD/Cheque",
    dd_cheque_number: "",
    dd_date: "",
    bank_name: "",
    remark: "",
  });

  const [successMessage, setSuccessMessage] = useState(""); // ← Success Modal State

  // Removed scheme related states
  const [showDropdown, setShowDropdown] = useState(false);
  const [bankSearch, setBankSearch] = useState("");
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [accountSearch, setAccountSearch] = useState("");
  const [showFeeDropdown, setShowFeeDropdown] = useState(false);
  const [feeSearch, setFeeSearch] = useState("");
  const [showDeveloperDropdown, setShowDeveloperDropdown] = useState(false);
  const [developerSearch, setDeveloperSearch] = useState("");
  const [showSchemeExtraDropdown, setShowSchemeExtraDropdown] = useState(false);
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const [paymentSearch, setPaymentSearch] = useState("");

  const [errors, setErrors] = useState({});
  const [banks, setBanks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [schemeDetailsList, setSchemeDetailsList] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [feeDescriptions, setFeeDescriptions] = useState([]);

  const paymentModes = ["Cash", "NEFT", "DD", "Cheque","RTGS"];

  /* ================= PRE-FILL FORM FOR EDIT ================= */
  useEffect(() => {
    if (isEdit && location.state?.record) {
      const record = location.state.record;
      setForm({
        receipt_no: record.receipt_no || "",
        receipt_date: record.receipt_date?.slice(0, 10) || new Date().toISOString().split("T")[0],
        challan_no: record.challan_no || "",
        account_no: record.account_no || "",
        developer_name: record.developer_name || "",
        amount: record.amount || "",
        amount_words: record.amount_words || "",
        fee_description: record.fee_description || "",
        proposal_no: record.proposal_no || "",
        scheme_details_extra: record.scheme_details_extra || "",
        payment_mode: record.payment_mode || "DD/Cheque",
        dd_cheque_number: record.dd_cheque_number || record.reference_no || "",
        dd_date: record.dd_date?.slice(0, 10) || "",
        bank_name: record.bank_name || "",
        remark: record.remark || "",
      });
    } else if (isEdit && !location.state?.record) {
      fetch(`${API_URL}/api/developer-charges/${id}`)
        .then((res) => res.json())
        .then((record) => {
          setForm({
            receipt_no: record.receipt_no || "",
            receipt_date: record.receipt_date?.slice(0, 10) || new Date().toISOString().split("T")[0],
            challan_no: record.challan_no || "",
            account_no: record.account_no || "",
            developer_name: record.developer_name || "",
            amount: record.amount || "",
            amount_words: record.amount_words || "",
            fee_description: record.fee_description || "",
            proposal_no: record.proposal_no || "",
            scheme_details_extra: record.scheme_details_extra || "",
            payment_mode: record.payment_mode || "DD/Cheque",
            dd_cheque_number: record.dd_cheque_number || "",
            dd_date: record.dd_date?.slice(0, 10) || "",
            bank_name: record.bank_name || "",
            remark: record.remark || "",
          });
        })
        .catch(() => setErrors({ general: "Failed to load record" }));
    }
  }, [isEdit, id]);

  /* ================= FETCH DROPDOWN DATA ================= */
  useEffect(() => {
    fetch(`${API_URL}/api/banks`).then(res => res.json()).then(data => setBanks(Array.isArray(data) ? data : []));
    fetch(`${API_URL}/api/account-numbers`).then(res => res.json()).then(data => setAccounts(Array.isArray(data) ? data : []));
    fetch(`${API_URL}/api/scheme-details`).then(res => res.json()).then(data => setSchemeDetailsList(Array.isArray(data) ? data : []));
    fetch(`${API_URL}/api/developers`).then(res => res.json()).then(data => setDevelopers(Array.isArray(data) ? data : []));
    fetch(`${API_URL}/api/fee-descriptions`).then(res => res.json()).then(data => setFeeDescriptions(Array.isArray(data) ? data : []));
  }, []);

  /* ================= NUMBER TO WORDS ================= */
  const numberToWords = (num) => {
    if (!num) return "";
    num = parseFloat(num);
    const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const inWords = (n) => {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n / 10)] + " " + a[n % 10];
      if (n < 1000) return a[Math.floor(n / 100)] + " Hundred " + inWords(n % 100);
      if (n < 100000) return inWords(Math.floor(n / 1000)) + " Thousand " + inWords(n % 1000);
      if (n < 10000000) return inWords(Math.floor(n / 100000)) + " Lakh " + inWords(n % 100000);
      return inWords(Math.floor(n / 10000000)) + " Crore " + inWords(n % 10000000);
    };
    return inWords(Math.floor(num)) + " Rupees Only";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updated = { ...form, [name]: value };

    if (name === "amount") {
      updated.amount_words = !isNaN(value) && value !== "" ? numberToWords(value) : "";
    }
    if (name === "receipt_date") {
      updated.dd_date = value;
    }
    setForm(updated);
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    let err = {};
    if (!form.developer_name.trim()) err.developer_name = "Developer name required";
    if (!form.amount || form.amount <= 0) err.amount = "Enter valid amount";
    if (!form.challan_no) err.challan_no = "Challan number required";
    if (!form.proposal_no) err.proposal_no = "Proposal number required";

    if ((form.payment_mode === "DD" || form.payment_mode === "Cheque") && !form.dd_cheque_number) {
      err.dd_cheque_number = "DD/Cheque number required";
    }
    return err;
  };
const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (Object.keys(err).length > 0) {
      setErrors(err);
      return;
    }

    try {
      const url = isEdit
        ? `${API_URL}/api/developer-charges/${id}`
        : `${API_URL}/api/developer-charges`;

      // ✅ CHANGE 1: build the payload with the date the user actually sees
      const payload = {
        ...form,
        dd_date: form.dd_date || form.receipt_date || null,
      };

      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        // ✅ CHANGE 2: send payload instead of form
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        let errorMessage = "Something went wrong";

        if (data.detail) {
          if (Array.isArray(data.detail)) {
            errorMessage = data.detail
              .map((err) => {
                const field = Array.isArray(err.loc) ? err.loc[err.loc.length - 1] : "Field";
                return `${field}: ${err.msg}`;
              })
              .join(" | ");
          } else if (typeof data.detail === "string") {
            errorMessage = data.detail;
          }
        }
        setErrors({ general: errorMessage });
        return;
      }

      // Success - Show Modal instead of alert
      setSuccessMessage(isEdit ? "Receipt updated successfully!" : "Receipt saved successfully!");

    } catch (err) {
      console.error(err);
      setErrors({ general: "Server connection error. Please try again." });
    }
  };
  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   const err = validate();
  //   if (Object.keys(err).length > 0) {
  //     setErrors(err);
  //     return;
  //   }

  //   try {
  //     const url = isEdit
  //       ? `${API_URL}/api/developer-charges/${id}`
  //       : `${API_URL}/api/developer-charges`;

  //     const res = await fetch(url, {
  //       method: isEdit ? "PUT" : "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(form),
  //     });

  //     if (!res.ok) {
  //       const data = await res.json();
  //       let errorMessage = "Something went wrong";

  //       if (data.detail) {
  //         if (Array.isArray(data.detail)) {
  //           errorMessage = data.detail
  //             .map((err) => {
  //               const field = Array.isArray(err.loc) ? err.loc[err.loc.length - 1] : "Field";
  //               return `${field}: ${err.msg}`;
  //             })
  //             .join(" | ");
  //         } else if (typeof data.detail === "string") {
  //           errorMessage = data.detail;
  //         }
  //       }
  //       setErrors({ general: errorMessage });
  //       return;
  //     }

  //     // Success - Show Modal instead of alert
  //     setSuccessMessage(isEdit ? "Receipt updated successfully!" : "Receipt saved successfully!");

  //   } catch (err) {
  //     console.error(err);
  //     setErrors({ general: "Server connection error. Please try again." });
  //   }
  // };

  // Close success modal and navigate
  const handleSuccessClose = () => {
    setSuccessMessage("");
    navigate("/developer-list");
  };

  return (
    <div className="pt-6 pb-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-2xl p-6 shadow mb-6">
        <h2 className="text-2xl font-semibold">
          {isEdit ? "Edit Developer Charges" : "Developer Charges"}
        </h2>
        <p className="text-sm opacity-90 mt-1">
          {isEdit ? "Update Developer Charges Receipt" : "Add Developer Charges Receipt"}
        </p>
      </div>

      {/* Error Message */}
      {errors.general && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm mb-6">
          {errors.general}
        </div>
      )}

      {/* Success Modal */}
      {successMessage && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-xl font-semibold mb-2">Success</h2>
            <p className="text-gray-600 mb-6">{successMessage}</p>
            <button
              onClick={handleSuccessClose}
              className="px-8 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-2xl shadow p-8">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">

          <Input label="Receipt No" value={form.receipt_no} disabled />
          <Input label="Receipt Date" type="date" name="receipt_date" value={form.receipt_date} onChange={handleChange} />

<Input label="Challan No" name="challan_no" value={form.challan_no} onChange={handleChange} error={errors.challan_no} required />

          {/* Account Number */}
          <div className="space-y-1.5 relative">
            <label className="text-sm font-medium text-gray-700">Account Number</label>
            <input
              type="text"
              name="account_no"
              value={form.account_no}
              onChange={(e) => { handleChange(e); setAccountSearch(e.target.value); setShowAccountDropdown(true); }}
              onFocus={() => setShowAccountDropdown(true)}
              onBlur={() => setTimeout(() => setShowAccountDropdown(false), 200)}
              placeholder="Type or select account number"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-blue-500"
            />
            {showAccountDropdown && accounts.length > 0 && (
              <div className="absolute z-50 w-full bg-white border rounded-xl shadow max-h-48 overflow-y-auto mt-1">
                {accounts.filter(acc => acc.toLowerCase().includes((accountSearch || "").toLowerCase()))
                  .map((acc, i) => (
                    <div key={i} onClick={() => { setForm({ ...form, account_no: acc }); setShowAccountDropdown(false); }}
                      className="px-4 py-2.5 hover:bg-gray-100 cursor-pointer">
                      {acc}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Developer Name */}
          <div className="space-y-1.5 relative">
  <label className="text-sm font-medium text-gray-700">
  Developer Name <span className="text-red-500">*</span>
</label>
            <input
              type="text"
              name="developer_name"
              value={form.developer_name}
              onChange={(e) => { handleChange(e); setDeveloperSearch(e.target.value); setShowDeveloperDropdown(true); }}
              onFocus={() => setShowDeveloperDropdown(true)}
              onBlur={() => setTimeout(() => setShowDeveloperDropdown(false), 200)}
              className={`w-full px-4 py-2.5 rounded-xl border bg-gray-50 focus:ring-2 focus:ring-blue-500 ${errors.developer_name ? "border-red-500" : "border-gray-300"}`}
            />
            {errors.developer_name && <p className="text-red-600 text-xs mt-1">{errors.developer_name}</p>}
            {showDeveloperDropdown && developers?.length > 0 && (
              <div className="absolute z-50 w-full bg-white border rounded-xl shadow max-h-48 overflow-y-auto mt-1">
                {developers.filter(d => d.toLowerCase().includes((developerSearch || "").toLowerCase()))
                  .map((d, i) => (
                    <div key={i} onClick={() => { setForm({ ...form, developer_name: d }); setShowDeveloperDropdown(false); }}
                      className="px-4 py-2.5 hover:bg-gray-100 cursor-pointer">
                      {d}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Fee Description */}
          <div className="space-y-1.5 relative">
            <label className="text-sm font-medium text-gray-700">Fee Description</label>
            <input
              type="text"
              name="fee_description"
              value={form.fee_description}
              onChange={(e) => { handleChange(e); setFeeSearch(e.target.value); setShowFeeDropdown(true); }}
              onFocus={() => setShowFeeDropdown(true)}
              onBlur={() => setTimeout(() => setShowFeeDropdown(false), 200)}
              placeholder="Type or select fee description"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-blue-500"
            />
            {showFeeDropdown && feeDescriptions?.length > 0 && (
              <div className="absolute z-50 w-full bg-white border rounded-xl shadow max-h-48 overflow-y-auto mt-1">
                {feeDescriptions.filter(f => f.toLowerCase().includes((feeSearch || "").toLowerCase()))
                  .map((f, i) => (
                    <div key={i} onClick={() => { setForm({ ...form, fee_description: f }); setShowFeeDropdown(false); }}
                      className="px-4 py-2.5 hover:bg-gray-100 cursor-pointer">
                      {f}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Scheme Details */}
          <div className="space-y-1.5 relative">
            <label className="text-sm font-medium text-gray-700">Scheme Details</label>
            <input
              name="scheme_details_extra"
              value={form.scheme_details_extra || ""}
              onChange={handleChange}
              onFocus={() => setShowSchemeExtraDropdown(true)}
              onBlur={() => setTimeout(() => setShowSchemeExtraDropdown(false), 200)}
              placeholder="Type or select scheme details"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-blue-500"
            />
            {showSchemeExtraDropdown && schemeDetailsList.length > 0 && (
              <div className="absolute z-50 w-full bg-white border rounded-xl shadow max-h-48 overflow-y-auto mt-1">
                {schemeDetailsList.filter(s => s.toLowerCase().includes((form.scheme_details_extra || "").toLowerCase()))
                  .map((s, i) => (
                    <div key={i} onClick={() => { setForm({ ...form, scheme_details_extra: s }); setShowSchemeExtraDropdown(false); }}
                      className="px-4 py-2.5 hover:bg-gray-100 cursor-pointer">
                      {s}
                    </div>
                  ))}
              </div>
            )}
          </div>

<Input label="Amount" name="amount" value={form.amount} onChange={handleChange} error={errors.amount} required />
          <Input label="Amount in Words" value={form.amount_words} disabled />

<Input label="Proposal No" name="proposal_no" value={form.proposal_no} onChange={handleChange} error={errors.proposal_no} required />
          {/* Payment Mode */}
          <div className="space-y-1.5 relative">
            <label className="text-sm font-medium text-gray-700">
    Payment Mode <span className="text-red-500">*</span>
  </label>
            <input
              type="text"
              name="payment_mode"
              value={form.payment_mode}
              onChange={(e) => { handleChange(e); setPaymentSearch(e.target.value); setShowPaymentDropdown(true); }}
              onFocus={() => setShowPaymentDropdown(true)}
              onBlur={() => setTimeout(() => setShowPaymentDropdown(false), 200)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-blue-500"
            />
            {showPaymentDropdown && (
              <div className="absolute z-50 w-full bg-white border rounded-xl shadow max-h-48 overflow-y-auto mt-1">
                {paymentModes.filter(p => p.toLowerCase().includes((paymentSearch || "").toLowerCase()))
                  .map((p, i) => (
                    <div key={i} onClick={() => { setForm({ ...form, payment_mode: p }); setShowPaymentDropdown(false); }}
                      className="px-4 py-2.5 hover:bg-gray-100 cursor-pointer">
                      {p}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Dynamic Payment Fields */}
          {(form.payment_mode === "DD" || form.payment_mode === "Cheque") && (
            <>
              <Input label="DD / Cheque No" name="dd_cheque_number" value={form.dd_cheque_number} onChange={handleChange} error={errors.dd_cheque_number} />
              <Input label="DD / Cheque Date" type="date" name="dd_date" value={form.dd_date || form.receipt_date} onChange={handleChange} />
            </>
          )}

          {(form.payment_mode === "Cash" || form.payment_mode === "NEFT" || form.payment_mode === "RTGS") && (
            <>
              <Input label={`${form.payment_mode} Transaction ID`} name="dd_cheque_number" value={form.dd_cheque_number} onChange={handleChange} />
              <Input label={`${form.payment_mode} Date`} type="date" name="dd_date" value={form.dd_date || form.receipt_date} onChange={handleChange} />
            </>
          )}

          {/* Bank Name */}
          <div className="space-y-1.5 relative">
            <label className="text-sm font-medium text-gray-700">Bank Name</label>
            <input
              type="text"
              name="bank_name"
              value={form.bank_name}
              onChange={(e) => { handleChange(e); setBankSearch(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              placeholder="Select or type bank"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-blue-500"
            />
            {showDropdown && banks.length > 0 && (
              <div className="absolute z-50 w-full bg-white border rounded-xl shadow max-h-48 overflow-y-auto mt-1">
                {banks.map((bank, i) => (
                  <div key={i} onClick={() => { setForm({ ...form, bank_name: bank }); setShowDropdown(false); }}
                    className="px-4 py-2.5 hover:bg-gray-100 cursor-pointer">
                    {bank}
                  </div>
                ))}
              </div>
            )}
          </div>

          <Input label="Remark" name="remark" value={form.remark} onChange={handleChange} />

          <div className="md:col-span-2 flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => navigate("/developer-list")} className="px-8 py-3.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition">
              Cancel
            </button>
            <button type="submit" className="px-12 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium shadow transition">
              {isEdit ? "Update Receipt" : "Save Receipt"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Input = ({ label, error, required, ...props }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-gray-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      {...props}
      className={`w-full px-4 py-2.5 rounded-xl border bg-gray-50 focus:ring-2 focus:ring-blue-500 transition
        ${error ? "border-red-500" : "border-gray-300"}
        ${props.disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    />
    {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
  </div>
);

export default DeveloperChargesAdd;